<!--#region DEBUG -->
@if (showDebugtools) {
  <details class="mb-3">
    <summary class="btn btn-sm btn-outline-secondary">View Pending Changes (Debug)</summary>
    <pre class="bg-light p-3 mt-2 small">{{ pendingChanges() | json }}</pre>
  </details>
}
<!--#endregion -->

<div class="container-fluid">
  <!--#region action buttons -->
  <div class="d-flex flex-column flex-md-row justify-content-between mb-2">
    <abc-month-year-selector
      class="mb-2 mb-md-0"
      [(month)]="selectedMonth"
      [(year)]="selectedYear"
      [pendingChanges]="hasPendingChanges()"
    />

    <abc-time-sheet-action-buttons
      [month]="selectedMonth()"
      [year]="selectedYear()"
      [tasks]="timeSheetData()"
      (save)="saveTimesheet()"
      (generate)="clearSelectedDay()"
      (complete)="clearSelectedDay()"
    />
  </div>
  <!--#endregion -->
  <!--#region table -->
  <div class="table-responsive mb-2">
    <table class="table table-bordered align-middle">
      <!--#region tableHeader -->
      <thead>
        <tr>
          <th scope="row" class="sticky-col bg-white">
            <button
              class="btn btn-sm btn-outline-secondary d-md-none"
              (click)="toggleCollapsed()"
              ngbTooltip="Hide/show task title"
              i18n-ngbTooltip="
                tooltip|Tooltip explaining table row header collapse@@collapseTableHeader"
            >
              @if (isHeaderCollapsed()) {
                <i class="bi-chevron-right"></i>
              } @else {
                <i class="bi-chevron-left"></i>
              }
            </button>
          </th>
          @for (day of daysOfMonth(); track $index) {
            <th
              style="min-width: 60px; text-align: center"
              scope="col"
              [title]="day | date: 'shortDate'"
              [class.bg-secondary-subtle]="isWeekend(day)"
            >
              <div class="fw-bold">{{ day.getDate() }}</div>
              <small class="text-muted">
                {{ day | date: 'EEE' }}
              </small>
            </th>
          }
        </tr>
      </thead>
      <!--#endregion -->

      <!--#region tableBody -->
      <tbody>
        @if (hasTimeSheetData()) {
          @for (task of timeSheetData(); track task.timesheetId) {
            <tr>
              <th scope="row" class="py-0 text-nowrap sticky-col bg-white">
                <div class="d-flex flex-row gap-2 justify-content-between align-items-center">
                  <span [title]="task.customerName + ' - ' + task.taskName">
                    @if (task.isProductive) {
                      <div style="font-size: x-small">
                        {{ task.customerName | truncateTitle: isHeaderCollapsed() : 3 }}
                      </div>
                      <div style="font-size: small">
                        {{ task.taskName | truncateTitle: isHeaderCollapsed() : 3 }}
                      </div>
                    } @else {
                      {{ task.taskName | truncateTitle: isHeaderCollapsed() : 3 }}
                    }
                  </span>
                  @if (!isHeaderCollapsed()) {
                    <abc-download-button
                      [task]="task"
                      [month]="selectedMonth()"
                      [year]="selectedYear()"
                    />
                  }
                </div>
              </th>

              @for (day of daysOfMonth(); track day.getTime()) {
                @if (shouldShowTimeInput(task, day)) {
                  <td class="p-0">
                    <input
                      #timeInput
                      type="text"
                      class="form-control border-0 rounded-0 shadow-none center-time time-input"
                      placeholder="--:--"
                      [class]="getCellClass(task, day)"
                      [value]="getTimeValue(task, day)"
                      [readonly]="getCellReadonly(task)"
                      [title]="getCellTitle(task, day)"
                      (click)="handleReadonlyClick(timeInput, task, day)"
                      (focus)="onCellFocus(task, day)"
                      (change)="onTimeChange(task, day, $event)"
                    />
                  </td>
                } @else {
                  <td class="table-danger"></td>
                }
              }
            </tr>
          }
        } @else {
          <tr>
            <td></td>
            <td [attr.colspan]="daysOfMonth().length" class="text-center py-5 text-muted">
              <div class="spinner-border me-2" role="status" aria-hidden="true"></div>
              <span
                i18n="
                  message|Loading message displayed while fetching the timesheet
                  data@@loadingTimesheet"
                >Loading timesheet…</span
              >
            </td>
          </tr>
        }
      </tbody>
      <!--#endregion -->

      <!--#region tableFooter -->
      <tfoot class="table-group-divider">
        <tr>
          <th scope="row" class="sticky-col text-nowrap">
            <strong>
              {{ dailyTotalHeaderTitle | truncateTitle: isHeaderCollapsed() : 3 }}
            </strong>
          </th>

          @for (day of daysOfMonth(); track day.getTime()) {
            <td
              class="text-center fw-bold"
              [class.bg-secondary-subtle]="isWeekend(day)"
              [class.bg-warning-subtle]="getDayTotal(day)?.notFilledIn"
              [class.text-danger]="getDayTotal(day)?.flagged"
            >
              {{ getDayTotal(day)?.time }}
            </td>
          }
        </tr>
        <tr>
          <th scope="row" class="sticky-col text-nowrap bg-light">
            <strong>
              {{ weeklyTotalHeaderTitle | truncateTitle: isHeaderCollapsed() : 3 }}
            </strong>
          </th>

          @for (cell of weekFooterCells(); track $index) {
            <td
              class="text-center fw-bold bg-light"
              [attr.colspan]="cell.span"
              [class.bg-warning-subtle]="cell.content.notFilledIn"
              [class.text-danger]="cell.content.flagged"
            >
              {{ cell.content.time }}
            </td>
          }
        </tr>
      </tfoot>
      <!--#endregion -->
    </table>
  </div>
  <!--#endregion -->

  <!--#region extraInfoCard  -->
  <abc-time-sheet-extra-info-card [selectedCell]="selectedCell()" />
  <!--#endregion -->
</div>
